import type {ReactElement} from "react";
import type {PaymentMethodField} from "../../../../domain/entities/PaymentMethod";
import {checkoutCopy} from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";
import {PaymentMethodFieldInput} from "./PaymentMethodFieldInput";

interface PaymentMethodFormProps {
    fields: PaymentMethodField[];
    values: Record<string, string>;
    onChange: (fieldId: string, value: string) => void;
}

export const PaymentMethodForm = ({fields, values, onChange}: PaymentMethodFormProps) => {
    if (!fields.length) {
        return null;
    }

    const renderFields = () => {
        const nodes: ReactElement[] = [];

        for (let index = 0; index < fields.length; index += 1) {
            const field = fields[index];
            const nextField = fields[index + 1];

            if (field.id === "dni" && nextField?.id === "dni_number") {
                const documentTypeField = field;
                const documentNumberField = nextField;

                nodes.push(
                    <div key="dni-group" className={styles.paymentFieldGroup}>
                        <label htmlFor={`payment-field-${documentNumberField.id}`} className={styles.paymentFieldLabel}>
                            {checkoutCopy.payment.fields.dniNumberLabel} *
                        </label>
                        <div className={styles.paymentSplitRow}>
                            <div className={styles.paymentFieldInline}>
                                <select
                                    id={`payment-field-${documentTypeField.id}`}
                                    name={documentTypeField.id}
                                    className={`${styles.paymentInput} ${styles.paymentSelect}`}
                                    value={values[documentTypeField.id] ?? "V"}
                                    onChange={(event) => onChange(documentTypeField.id, event.target.value)}
                                    required={documentTypeField.required}
                                    aria-label={documentTypeField.label}
                                >
                                    {documentTypeField.options?.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.paymentFieldInline}>
                                <input
                                    id={`payment-field-${documentNumberField.id}`}
                                    name={documentNumberField.id}
                                    className={styles.paymentInput}
                                    type="text"
                                    value={values[documentNumberField.id] ?? ""}
                                    onChange={(event) =>
                                        onChange(documentNumberField.id, event.target.value.replace(/\D+/g, ""))
                                    }
                                    placeholder={documentNumberField.placeholder}
                                    required={documentNumberField.required}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    aria-label={documentNumberField.label}
                                />
                            </div>
                        </div>
                    </div>,
                );
                index += 1;
                continue;
            }

            if (field.id === "phone_prefix" && nextField?.id === "phone_number") {
                const phonePrefixField = field;
                const phoneNumberField = nextField;

                nodes.push(
                    <div key="phone-group" className={styles.paymentFieldGroup}>
                        <label htmlFor={`payment-field-${phoneNumberField.id}`} className={styles.paymentFieldLabel}>
                            {checkoutCopy.payment.fields.phoneLabel} *
                        </label>
                        <div className={styles.paymentSplitRow}>
                            <div className={styles.paymentFieldInline}>
                                <select
                                    id={`payment-field-${phonePrefixField.id}`}
                                    name={phonePrefixField.id}
                                    className={`${styles.paymentInput} ${styles.paymentSelect}`}
                                    value={values[phonePrefixField.id] ?? "0424"}
                                    onChange={(event) => onChange(phonePrefixField.id, event.target.value)}
                                    required={phonePrefixField.required}
                                    aria-label={phonePrefixField.label}
                                >
                                    {phonePrefixField.options?.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.paymentFieldInline}>
                                <input
                                    id={`payment-field-${phoneNumberField.id}`}
                                    name={phoneNumberField.id}
                                    className={styles.paymentInput}
                                    type="tel"
                                    value={values[phoneNumberField.id] ?? ""}
                                    onChange={(event) =>
                                        onChange(phoneNumberField.id, event.target.value.replace(/\D+/g, ""))
                                    }
                                    placeholder={phoneNumberField.placeholder}
                                    required={phoneNumberField.required}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    aria-label={phoneNumberField.label}
                                />
                            </div>
                        </div>
                    </div>,
                );
                index += 1;
                continue;
            }

            nodes.push(
                <PaymentMethodFieldInput
                    key={field.id}
                    field={field}
                    value={values[field.id] ?? ""}
                    onChange={onChange}
                />,
            );
        }

        return nodes;
    };

    return (
        <section className={styles.paymentForm} aria-labelledby="payment-details-title">
            <h3 id="payment-details-title" className={styles.paymentFormTitle}>
                {checkoutCopy.payment.detailsHeading}
            </h3>
            <div className={styles.paymentFormFields}>
                {renderFields()}
            </div>
        </section>
    );
};
